// CoachConfig: the side model that keeps the information map's ledger and
// slips Otto one-line notes, plus the drafter that writes the checkpoint and
// the read-back (design 5.3, 5.5). The instruction text is data (v0 drafts
// until Wes redefines them); the limits are enforced in code by the brain.
import { z } from "zod";
import { DraftLabel, Id, behaviorText } from "./common";

/** Flags the coach may raise from the transcript (design 5.3). */
export const CoachFlag = z.enum([
  "stressed",
  "terse",
  "skeptical",
  "humor_used",
  "asked_for_human",
  "asked_about_price",
  "language_switched",
  "channel_detected",
  "abusive",
  "prankster",
  "volunteered_personal_details",
]);
export type CoachFlag = z.infer<typeof CoachFlag>;

export const DrafterConfig = z.object({
  /** The DRAFTER_MODEL switch overrides this at run time. */
  model_id: z.string().min(1).default("gemini-3.8-flash"),
  checkpoint_instructions: z.string().min(1).max(20_000),
  readback_instructions: z.string().min(1).max(20_000),
  checkpoint_max_sentences: z.number().int().min(1).max(5).default(2),
  /** Design 5.5: 40 to 60 words. */
  readback_max_words: z.number().int().min(10).max(150).default(60),
  /** One redraft, then a plain read-back assembled by code (design 5.5). */
  max_redrafts: z.number().int().min(0).max(3).default(1),
});
export type DrafterConfig = z.infer<typeof DrafterConfig>;

/**
 * The package writer (design 8.2): the model that turns the finished
 * transcript and ledger into the ConsultationPackage, after the call. Its
 * instructions are behavior text, so they are versioned data here
 * (integration, Sept 28 2026; before this they were an unversioned setting).
 * Without them the writer is skipped, loudly, and the package takes the
 * ledger-only or transcript-only path.
 */
export const WriterConfig = z.object({
  instructions: z.string().min(1).max(40_000),
});
export type WriterConfig = z.infer<typeof WriterConfig>;

const MarkerList = z.array(z.string().min(1).max(80)).max(40);

/**
 * Phrases the deterministic scorecard looks for (folded match) to find the
 * close and the priority questions (design 10.1). They follow the brief's
 * wording, so they are data next to it: change the close, change these.
 * Absent: the scorer's built in v0 markers (@otto/guard DEFAULT_MARKERS).
 */
export const ScoreMarkersConfig = z.object({
  close: z.object({
    excited: MarkerList,
    assessment: MarkerList,
    follow_up: MarkerList,
    thanks: MarkerList,
    goodbye: MarkerList,
  }),
  beats: z.record(Id, MarkerList),
});
export type ScoreMarkersConfig = z.infer<typeof ScoreMarkersConfig>;

export const CoachConfig = z.object({
  draft_label: DraftLabel,
  model_id: z.string().min(1).default("gemini-3.5-flash-lite"),
  /** The coach's instructions. Put the static part first so the provider's cache can hit (design 5.3 item 5). */
  instructions: z.string().min(1).max(40_000),
  /** A hint is 25 words or less, phrased as an intention (design 5.3). */
  max_hint_words: z.number().int().min(5).max(60).default(25),
  /** Starts at 8 s; set from the coach's measured p90 after test T1. */
  timeout_ms: z.number().int().min(500).max(30_000).default(8000),
  /** This many failures in a row switch the coach off for that consult. */
  max_consecutive_failures: z.number().int().min(1).max(10).default(3),
  max_notes_per_otto_turn: z.number().int().min(0).max(3).default(1),
  /** No coach notes during these stages (the read-back and the close). Must be agenda stage ids. */
  silent_stage_ids: z.array(Id).default([]),
  /** After Otto uses humor, this many turns carry "no humor this turn" (design 5.6). */
  humor_cooldown_turns: z.number().int().min(0).max(10).default(3),
  /** Any of these flags switches humor off for the rest of the call. */
  humor_off_flags: z.array(CoachFlag).default(["stressed"]),
  /**
   * The abusive turn that makes code close the call (design 5.8, "code counts
   * strikes"). The default 2 is the rule before Oct 12 2026: one boundary,
   * then the close. Wes, Oct 2 2026: a warning only for abuse aimed at Otto or
   * the team, and more than one warning before ending, so the v1 coach sets 3.
   */
  abusive_strikes_to_close: z.number().int().min(2).max(5).default(2),
  /**
   * Information map keys only the clock asks about (Oct 14 2026: the closing
   * questions, near the end). A coach hint aimed at one is never sent; in the
   * rehearsal the coach pointed Otto at them at minute seven and he closed.
   */
  clock_only_keys: z.array(z.string().regex(/^[a-z][a-z0-9_]{0,63}$/)).max(20).default([]),
  /**
   * Optional. The line the coach's note carries when humor is off for this
   * turn or the call (design 5.6). Absent: humor is still switched off in
   * the coach's context, but no extra line goes to Otto.
   */
  no_humor_note: behaviorText(300).optional(),
  drafter: DrafterConfig,
  writer: WriterConfig.optional(),
  score_markers: ScoreMarkersConfig.optional(),
});
export type CoachConfig = z.infer<typeof CoachConfig>;
